import Grid from "@mui/material/Grid"
import Box from "@mui/material/Box"
import React from "react"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiTextField from "components/MuiTextField"
import {
    Button,
    InputAdornment,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import useSurveyBasicDetails, {
    CHARGER_DETAIL_COLUMNS,
    ESurveyBasicDetailFields,
    ESurveyChargerDetailFields,
    SurveyChargerDetailRow,
} from "./useSurveyBasicDetails"
import { ProjectFormPageProps } from "components/ProjectForm"
import { EProjectFormPages } from "components/ProjectForm/config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrashXmark } from "@fortawesome/pro-solid-svg-icons"
import { DropdownObj } from "pages/Common/commonTypes"
import ProjectFormFooter from "components/ProjectForm/components/ProjectFormFooter"
import FileUploaderV2 from "components/FileUploaderV2"

const headerCellSx = { color: "#70758D", fontWeight: 600, fontSize: 12 }
const transparentInputSx = {
    width: "100%",
    "& .MuiInputBase-root": {
        width: "100%",
        backgroundColor: "transparent",
        "& fieldset": { border: "none" },
    },
    "& .MuiInputBase-input": { backgroundColor: "transparent" },
}

const SurveyBasicDetails = (props: ProjectFormPageProps) => {
    const { states, functions } = useSurveyBasicDetails(props)
    const isLastPage = !props.pages?.includes(EProjectFormPages.PAYMENT_DETAILS)
    const {
        formik,
        businessPocOptions,
        categoryOptions,
        isCategoriesFetching,
        timeSlotOptions,
        CHARGER_TYPE_OPTIONS,
        CONNECTION_TYPE_OPTIONS,
        CONNECTION_OWNED_BY_OPTIONS,
        FOUNDATION_TYPE_OPTIONS,
        isSubmitting,
        spotImageNames,
        electricityBillNames,
    } = states
    const { handleAddChargerRow, handleDeleteChargerRow, updateChargerRow, handleAssignToMe, hasError, hasChargerError, handleChange, handleDropdownChange, handleSpotImageChange, handleElectricityBillChange } = functions

    const renderCell = (col: any, row: SurveyChargerDetailRow, rowIndex: number) => {
        if (col.key === "index") return rowIndex + 1

        if (col.key === "actions") {
            return (
                <Box display="flex" gap={1} alignItems="center" padding="4px 8px">
                    {rowIndex > 0 && (
                        <Box
                            width="20px"
                            height="20px"
                            display="flex"
                            justifyContent="center"
                            borderRadius="50%"
                            bgcolor="#F940541A"
                            alignItems="center"
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleDeleteChargerRow(rowIndex)}>
                            <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="sm" />
                        </Box>
                    )}
                </Box>
            )
        }

        const value = row[col.key as keyof SurveyChargerDetailRow]

        if (col.type === "dropdown") {
            const isError = hasChargerError(rowIndex, col.key as ESurveyChargerDetailFields)
            return (
                <Select
                    fullWidth
                    displayEmpty
                    value={(value as DropdownObj)?.id ?? ""}
                    onChange={e => {
                        const selected = CHARGER_TYPE_OPTIONS.find(opt => opt.id === String(e.target.value)) || null
                        updateChargerRow(rowIndex, col.key as ESurveyChargerDetailFields, selected)
                    }}
                    sx={{
                        borderRadius: 0,
                        fontSize: 12,
                        "& .MuiOutlinedInput-notchedOutline": { border: isError ? "1px solid #F94054" : "none" },
                        "& .MuiSelect-select": { padding: "4px 8px" },
                        ...(isError && { color: "#F94054" }),
                    }}>
                    <MenuItem value="" disabled>
                        Select type
                    </MenuItem>
                    {CHARGER_TYPE_OPTIONS.map(opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.title}
                        </MenuItem>
                    ))}
                </Select>
            )
        }

        if (col.type === "number") {
            const isError = hasChargerError(rowIndex, col.key as ESurveyChargerDetailFields)
            return (
                <MuiTextField
                    fullWidth
                    type="number"
                    value={value as string}
                    error={isError}
                    onChange={e => updateChargerRow(rowIndex, col.key as ESurveyChargerDetailFields, e.target.value)}
                    slotProps={
                        col.unit
                            ? {
                                  input: {
                                      endAdornment: (
                                          <InputAdornment className="text-sm color-base-grey" position="end">
                                              ({col.unit})
                                          </InputAdornment>
                                      ),
                                  },
                                  htmlInput: { min: 0 },
                              }
                            : { htmlInput: { min: 0 } }
                    }
                    sx={transparentInputSx}
                />
            )
        }

        return null
    }

    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{ overflowY: "auto" }}>
                <Grid container spacing={4}>
                    {/* Survey Name */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Survey Name*</Typography>
                            <MuiTextField
                                size="large"
                                placeholder="Enter Survey Name"
                                value={formik.values[ESurveyBasicDetailFields.SURVEY_NAME]}
                                onChange={handleChange}
                                name={ESurveyBasicDetailFields.SURVEY_NAME}
                                error={hasError(ESurveyBasicDetailFields.SURVEY_NAME)}
                            />
                        </Box>
                    </Grid>

                    {/* Project Category */}
                    <Grid size={6}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Project Category*</Typography>
                            <MuiAutoComplete
                                size="large"
                                options={categoryOptions}
                                loading={isCategoriesFetching}
                                value={formik.values[ESurveyBasicDetailFields.CATEGORY]}
                                onChange={val => handleDropdownChange(ESurveyBasicDetailFields.CATEGORY, val)}
                                placeholder="Select Project Category"
                                error={hasError(ESurveyBasicDetailFields.CATEGORY)}
                            />
                        </Box>
                    </Grid>

                    {/* Deal Done By (Business POC) */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Deal Done By (Business POC)*</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={businessPocOptions}
                                value={formik.values[ESurveyBasicDetailFields.BUSINESS_POC]}
                                onChange={val => handleDropdownChange(ESurveyBasicDetailFields.BUSINESS_POC, val)}
                                placeholder="Select Name"
                                error={hasError(ESurveyBasicDetailFields.BUSINESS_POC)}
                            />
                            <Typography
                                variant="label"
                                fontSize={"10px"}
                                textTransform={"none"}
                                onClick={handleAssignToMe}
                                sx={{
                                    textDecoration: "underline",
                                    textUnderlineOffset: "2px",
                                    fontWeight: "500",
                                    color: "#0430C0",
                                    cursor: "pointer",
                                }}>
                                Assign to me
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Charger Details Table */}
                    <Grid size={12}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Typography textTransform={"uppercase"} fontWeight={500} color="#FC5735">
                                Charger Details*
                            </Typography>
                            <TableContainer>
                                <Table
                                    size="small"
                                    sx={{
                                        borderCollapse: "collapse",
                                        "& td, & th": { border: "1px solid #E5E5E5" },
                                    }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerCellSx}>S.No.</TableCell>
                                            {CHARGER_DETAIL_COLUMNS.map((col, i) => (
                                                <TableCell key={i} sx={headerCellSx}>
                                                    {col.label}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={headerCellSx}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formik.values[ESurveyBasicDetailFields.CHARGER_DETAILS].map(
                                            (row, rowIndex) => (
                                                <TableRow key={rowIndex}>
                                                    <TableCell sx={{ p: "4px 8px" }}>{rowIndex + 1}</TableCell>
                                                    {CHARGER_DETAIL_COLUMNS.map((col, colIndex) => (
                                                        <TableCell
                                                            key={colIndex}
                                                            sx={{
                                                                backgroundColor: "#EFEFEF",
                                                            }}>
                                                            {renderCell(col, row, rowIndex)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ p: "4px 8px" }}>
                                                        <Box
                                                            width="20px"
                                                            height="20px"
                                                            display="flex"
                                                            justifyContent="center"
                                                            borderRadius="50%"
                                                            bgcolor="#F940541A"
                                                            alignItems="center"
                                                            sx={{
                                                                cursor: "pointer",
                                                                visibility:
                                                                    formik.values[ESurveyBasicDetailFields.CHARGER_DETAILS].length > 1
                                                                        ? "visible"
                                                                        : "hidden",
                                                            }}
                                                            onClick={() => handleDeleteChargerRow(rowIndex)}>
                                                            <FontAwesomeIcon
                                                                icon={faTrashXmark}
                                                                color="#F94054"
                                                                size="sm"
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box display="flex" alignItems="center" justifyContent="center">
                                <Button onClick={handleAddChargerRow} variant="outlined" color='statiqBlue'>
                                    + Add New
                                </Button>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Other Details Section Header */}
                    <Grid size={12}>
                        <Typography textTransform={"uppercase"} fontWeight={500} color="#FC5735">
                            Other Details
                        </Typography>
                    </Grid>

                    {/* Available Load | Connection Type */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Available Load</Typography>
                            <MuiTextField
                                size="large"
                                type="number"
                                placeholder="Enter Available Load"
                                name={ESurveyBasicDetailFields.AVAILABLE_LOAD}
                                value={formik.values[ESurveyBasicDetailFields.AVAILABLE_LOAD]}
                                onChange={formik.handleChange}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment  position="end">
                                                <Typography className="text-sm">KWH</Typography>
                                            </InputAdornment>
                                        ),
                                    },
                                    htmlInput: { min: 0 },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Connection Type</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={CONNECTION_TYPE_OPTIONS}
                                value={formik.values[ESurveyBasicDetailFields.CONNECTION_TYPE]}
                                onChange={val => formik.setFieldValue(ESurveyBasicDetailFields.CONNECTION_TYPE, val)}
                                placeholder="Select Connection Type"
                            />
                        </Box>
                    </Grid>

                    {/* Connection Owned By | Number of Spots */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Connection Owned By</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={CONNECTION_OWNED_BY_OPTIONS}
                                value={formik.values[ESurveyBasicDetailFields.CONNECTION_OWNED_BY]}
                                onChange={val =>
                                    formik.setFieldValue(ESurveyBasicDetailFields.CONNECTION_OWNED_BY, val)
                                }
                                placeholder="Select Connection Owned By"
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Number Of Spots</Typography>
                            <MuiTextField
                                size="large"
                                type="number"
                                placeholder="Enter Number of Spots"
                                name={ESurveyBasicDetailFields.NO_OF_SPOTS}
                                value={formik.values[ESurveyBasicDetailFields.NO_OF_SPOTS]}
                                onChange={formik.handleChange}
                                slotProps={{ htmlInput: { min: 0 } }}
                            />
                        </Box>
                    </Grid>

                    {/* Spot Images | Electricity Bill */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Spot Images</Typography>
                            <FileUploaderV2
                                maxFiles={3}
                                accept=".jpg,.jpeg,.pdf,.png"
                                onChange={handleSpotImageChange}
                                initialFileCount={spotImageNames.length}
                                existingFileNames={spotImageNames}
                            />
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="label" fontSize="10px" color="#70758D" textTransform="none">
                                    Upto 3 Images
                                </Typography>
                                <Typography variant="label" fontSize="10px" color="#70758D" textTransform="none">
                                    File Type: JPG, PDF, PNG
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Electricity Bill</Typography>
                            <FileUploaderV2
                                maxFiles={3}
                                accept=".jpg,.jpeg,.pdf,.png"
                                onChange={handleElectricityBillChange}
                                initialFileCount={electricityBillNames.length}
                                existingFileNames={electricityBillNames}
                            />
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="label" fontSize="10px" color="#70758D" textTransform="none">
                                    Upto 3 Images
                                </Typography>
                                <Typography variant="label" fontSize="10px" color="#70758D" textTransform="none">
                                    File Type: JPG, PDF, PNG
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Survey Visit By Date | Time Slot */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Survey Visit By Date*</Typography>
                            <MuiTextField
                                size="large"
                                type="date"
                                name={ESurveyBasicDetailFields.VISIT_BY_DATE}
                                value={formik.values[ESurveyBasicDetailFields.VISIT_BY_DATE]}
                                onChange={handleChange}
                                error={hasError(ESurveyBasicDetailFields.VISIT_BY_DATE)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Time Slot*</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={timeSlotOptions}
                                value={formik.values[ESurveyBasicDetailFields.TIME_SLOT]}
                                onChange={val => handleDropdownChange(ESurveyBasicDetailFields.TIME_SLOT, val)}
                                placeholder="Select Time Slot"
                                error={hasError(ESurveyBasicDetailFields.TIME_SLOT)}
                            />
                        </Box>
                    </Grid>

                    {/* Foundation Type */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Foundation Type</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={FOUNDATION_TYPE_OPTIONS}
                                value={formik.values[ESurveyBasicDetailFields.FOUNDATION_TYPE]}
                                onChange={val => formik.setFieldValue(ESurveyBasicDetailFields.FOUNDATION_TYPE, val)}
                                placeholder="Select Foundation Type"
                            />
                        </Box>
                    </Grid>

                    {/* Remarks */}
                    <Grid size={12}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Remarks</Typography>
                            <MuiTextField
                                size="large"
                                multiline
                                placeholder="Enter Remarks"
                                name={ESurveyBasicDetailFields.REMARKS}
                                value={formik.values[ESurveyBasicDetailFields.REMARKS]}
                                onChange={formik.handleChange}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                isLoading={isSubmitting}
                handleSubmit={() => formik.handleSubmit()}
                submitButtonText={isLastPage ? "Submit" : "Next"}
            />
        </>
    )
}

export default SurveyBasicDetails
